import React, { useState, useMemo } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import LunchMenu from './LunchMenu';
import MyOrder from './MyOrder';
import LunchSummary from './LunchSummary';
import EmployeeOrder from './EmployeeOrder';
import LunchOrders from './LunchOrders';
import { LUNCH, ROLES } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lunch-tabpanel-${index}`}
      aria-labelledby={`lunch-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Lunch: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Base tabs available to all users
  const baseTabs: Array<{ label: string; component: React.ReactElement }> = [
    { label: LUNCH.TABS.ORDER, component: <MyOrder /> },
    { label: LUNCH.TABS.ORDER_FOR_OTHER, component: <EmployeeOrder /> },
    { label: LUNCH.TABS.DAILY_ORDERS, component: <LunchOrders /> },
    { label: LUNCH.TABS.MONTHLY_SUMMARY, component: <LunchSummary /> },
  ];

  // Add Daily Menu tab at the beginning if user is admin
  const tabs = useMemo(() => {
    const tabsArray = [...baseTabs];
    if (isAdmin) {
      tabsArray.unshift({ label: LUNCH.TABS.DAILY_MENU, component: <LunchMenu /> });
    }
    return tabsArray;
  }, [isAdmin]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Lunch Management
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="lunch tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={tabValue} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

export default Lunch;
